/**
 * Numeric column alignment + display grouping (PP-01).
 *
 * Shared by BOTH results grids — `common/components/ResultsGrid.tsx`
 * (Query Editor / Notebook / FK preview) and
 * `table-explorer/components/TableExplorerGrid.tsx`. Keep the two grids in
 * parity: anything applied here must be wired into both render paths.
 *
 * HOW candidate columns render is a global user setting
 * (`useAppStore.numericGrouping`, a `NumericGroupingMode`): locale grouping
 * (default), thin-space grouping, or off. WHICH columns are candidates
 * (`shouldGroupNumericColumn`) never depends on the setting. This module owns
 * the mode's semantics but stays store-free — callers read the mode once per
 * grid and pass it in.
 *
 * **This module is presentation-only.** `formatNumericDisplay` must never be
 * reachable from `buildCopyText` / `buildCopyHtml` / `csvClipboard` / the
 * export pipeline — copy and export emit RAW values. A grouped `1,234,567.89`
 * pasted into an INSERT or a CSV is silently wrong data.
 */

/**
 * The column metadata both grids can supply. Every field is optional because
 * the two grids normalize columns differently (Query Editor result-set columns
 * have no `isIdentity`; session-restored tabs can have no `dataType` at all)
 * and this runs per column on the render hot path — a missing field must
 * degrade, never throw.
 */
export type AlignmentColumnMeta = {
  name?: string | null;
  /** Engine-specific type string, e.g. `bigint`, `decimal(18,2)`, `NUMBER(10,2)`. */
  dataType?: string | null;
  isPrimaryKey?: boolean | null;
  /** Real FK constraint OR a user-declared loose relationship. */
  isForeignKey?: boolean | null;
  /** IDENTITY / AUTO_INCREMENT / serial-backed column (Table Explorer only). */
  isIdentity?: boolean | null;
};

/**
 * Base type names that are numeric in at least one supported engine.
 * Matching is **exact on normalized words**, never substring: `int` as a
 * substring also appears in `point`, `interval` and `constraint`, and the
 * results grid already has a regression test pinning `POINT` as non-numeric.
 */
const NUMERIC_BASE_TYPES: ReadonlySet<string> = new Set([
  // integers — MSSQL / PG / MySQL / SQLite
  'int', 'integer', 'int2', 'int4', 'int8', 'smallint', 'tinyint', 'mediumint', 'bigint',
  // PG auto-increment spellings
  'serial', 'serial2', 'serial4', 'serial8', 'smallserial', 'bigserial',
  // exact numerics — incl. MySQL `fixed`, Oracle `number`, MSSQL money
  'decimal', 'dec', 'numeric', 'number', 'fixed', 'money', 'smallmoney',
  // approximate numerics — incl. Oracle binary floats
  'float', 'float4', 'float8', 'real', 'double', 'double precision',
  'binary_float', 'binary_double',
  // Kusto CSL scalar (`long`); `int`/`real`/`decimal` above cover the rest
  'long',
  // MySQL YEAR is numeric-typed (right-aligned) but never grouped — see
  // `shouldGroupNumericColumn`.
  'year',
]);

/** Type words that only qualify a base type and are dropped before matching. */
const TYPE_MODIFIER_WORDS: ReadonlySet<string> = new Set(['unsigned', 'signed', 'zerofill']);

/**
 * Trailing name tokens that mark a column as an identifier or an ordinal
 * rather than a quantity. Grouping `123,456,789` for a 9-digit key, or
 * `2,026` for a year, is actively wrong — it invents structure the value
 * does not have and makes keys unrecognisable.
 */
const IDENTIFIER_NAME_TOKENS: ReadonlySet<string> = new Set([
  'id', 'ids', 'uid', 'uuid', 'guid', 'key', 'code', 'no', 'num', 'nr',
  'year', 'yr', 'version',
]);

/** Lowercased base type: parens/modifiers stripped, whitespace collapsed. */
function normalizeDataType(raw: string | null | undefined): string {
  return (typeof raw === 'string' ? raw : '')
    .trim()
    .toLowerCase()
    .replace(/\(.*$/u, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
}

/**
 * True when the column's engine type is numeric. Decided from column
 * METADATA, never by sniffing values — a text column that happens to hold
 * digits in the loaded window must stay left-aligned.
 */
export function isNumericColumn(column: AlignmentColumnMeta): boolean {
  const raw = (typeof column.dataType === 'string' ? column.dataType : '').trim();
  if (!raw) return false;
  // Array types (PG `int[]`, `numeric[][]`) render as bracketed lists — the
  // element type must not leak into the alignment decision.
  if (/\[\s*\]\s*$/u.test(raw)) return false;

  const base = normalizeDataType(raw);
  if (!base) return false;
  if (NUMERIC_BASE_TYPES.has(base)) return true;

  const words = base.split(' ').filter((w) => w && !TYPE_MODIFIER_WORDS.has(w));
  if (words.length === 0) return false;
  // `int unsigned` → `int`; `unsigned big int` → `big int` → the `int` word.
  if (NUMERIC_BASE_TYPES.has(words.join(' '))) return true;
  // Only the LAST word names the base type; a word in front of it qualifies it.
  // Oracle `LONG RAW` is binary, and `INTERVAL YEAR(2) TO MONTH` (cut to
  // `interval year` above) is a duration — neither is a number.
  return words[0] !== 'interval' && NUMERIC_BASE_TYPES.has(words[words.length - 1]);
}

/**
 * Split a column name into words across snake_case, kebab-case, spaces and
 * camelCase/PascalCase boundaries, and return the last one lowercased.
 * `customerId` → `id`, `CUSTOMER_ID` → `id`, `num_orders` → `orders`.
 */
function trailingNameToken(name: string | null | undefined): string {
  const words = (typeof name === 'string' ? name : '')
    .trim()
    .replace(/[^A-Za-z0-9]+/gu, ' ')
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .split(' ')
    .filter(Boolean);
  return (words[words.length - 1] ?? '').toLowerCase();
}

/**
 * True when a numeric column should get thousands grouping in the grid.
 *
 * **The rule:** every numeric column is right-aligned, but only *quantities*
 * are grouped. A column is treated as an identifier or ordinal — and left
 * ungrouped — when either:
 *
 * 1. **Structural signals** say it is a key: primary key, identity /
 *    auto-increment, or foreign key (real constraint or declared loose
 *    relationship). These are engine-agnostic and come straight from
 *    metadata, so they hold for all five engines including Oracle, where
 *    every key is a bare `NUMBER`.
 * 2. **Name signals**: the trailing token is one of `id`/`ids`/`uid`/`uuid`/
 *    `guid`/`key`/`code`/`no`/`num`/`nr`/`year`/`yr`/`version`. Only the
 *    TRAILING token counts, so `num_orders` and `year_total` still group.
 * 3. **Type signals**: MySQL `YEAR`.
 */
export function shouldGroupNumericColumn(column: AlignmentColumnMeta): boolean {
  if (!isNumericColumn(column)) return false;
  if (column.isPrimaryKey || column.isIdentity || column.isForeignKey) return false;
  if (normalizeDataType(column.dataType) === 'year') return false;
  return !IDENTIFIER_NAME_TOKENS.has(trailingNameToken(column.name));
}

/**
 * How a grouping-candidate column renders its integer digits.
 * - `locale` — `Intl` grouping with the locale's separators (`1,234,567.89`).
 * - `thin-space` — every 3 digits from the right separated by U+202F NARROW
 *   NO-BREAK SPACE, decimal point kept verbatim (`1 234 567.89`): reads as
 *   grouping at a glance but can never be mistaken for a `FORMAT()` result or
 *   a European decimal comma.
 * - `off` — the raw value string, exactly as the driver returned it.
 */
export type NumericGroupingMode = 'locale' | 'thin-space' | 'off';

export const NUMERIC_GROUPING_MODES: readonly NumericGroupingMode[] = ['locale', 'thin-space', 'off'];

/** U+202F NARROW NO-BREAK SPACE — the `thin-space` group separator. */
export const THIN_SPACE_SEPARATOR = '\u202F';

const groupFormatterCache = new Map<string, Intl.NumberFormat>();
const decimalSeparatorCache = new Map<string, string>();

function localeKey(locale: string | undefined): string {
  return locale ?? '';
}

function groupFormatter(locale: string | undefined): Intl.NumberFormat {
  const key = localeKey(locale);
  let formatter = groupFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 });
    groupFormatterCache.set(key, formatter);
  }
  return formatter;
}

function decimalSeparator(locale: string | undefined): string {
  const key = localeKey(locale);
  let separator = decimalSeparatorCache.get(key);
  if (separator === undefined) {
    separator =
      new Intl.NumberFormat(locale).formatToParts(1.1).find((p) => p.type === 'decimal')?.value ??
      '.';
    decimalSeparatorCache.set(key, separator);
  }
  return separator;
}

/** Plain decimal notation only — exponents, hex and separators bail out. */
const PLAIN_DECIMAL_RE = /^([+-]?)(\d+)(?:\.(\d+))?$/u;

/** `1234567` → `1 234 567` — plain 3-digit chunks from the right, no `Intl`. */
function groupWithThinSpace(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+$)/gu, THIN_SPACE_SEPARATOR);
}

/**
 * Format a cell value with thousands grouping for DISPLAY ONLY.
 *
 * Returns `null` whenever the value must be rendered untouched — `null` /
 * `undefined` / empty / `NaN` / `Infinity` / non-numeric text / exponential
 * notation / zero-padded digits, and ALWAYS when `mode` is `'off'` (callers
 * should not even call in that case — the grouped column set is empty). Callers
 * fall back to their existing `String(value)` rendering on `null`, so an
 * unrecognised shape can never be mangled.
 *
 * Decimals are preserved **exactly** as the driver returned them: the
 * fraction is carried across verbatim (no rounding, no padding) and the
 * integer part is grouped from its digit string via `Intl` + `BigInt`
 * (`locale`) or plain 3-digit chunking (`thin-space`), so a `bigint` beyond
 * 2^53 keeps every digit.
 */
export function formatNumericDisplay(
  value: unknown,
  mode: NumericGroupingMode = 'locale',
  locale?: string,
): string | null {
  if (mode === 'off') return null;
  let raw: string;
  if (value === null || value === undefined) return null;
  if (typeof value === 'bigint') {
    raw = value.toString();
  } else if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    raw = String(value);
  } else if (typeof value === 'string') {
    raw = value.trim();
    if (raw === '') return null;
  } else {
    return null;
  }

  const match = PLAIN_DECIMAL_RE.exec(raw);
  if (!match) return null;
  const [, sign, digits, fraction] = match;
  // A zero-padded integer carries meaning we must not destroy (`007` ≠ `7`).
  if (digits.length > 1 && digits.startsWith('0')) return null;

  const grouped = mode === 'thin-space'
    ? groupWithThinSpace(digits)
    : groupFormatter(locale).format(BigInt(digits));
  const negative = sign === '-';
  const head = negative ? `-${grouped}` : grouped;
  if (fraction === undefined) return head;
  return `${head}${mode === 'thin-space' ? '.' : decimalSeparator(locale)}${fraction}`;
}
